/**
 * Freie Zeitraumwahl ueber zwei Monate.
 *
 * Erster Klick setzt den Beginn, zweiter das Ende, der dritte beginnt neu. Das ist vorhersehbar,
 * auch ueber Monatsgrenzen — die Standardlogik von react-day-picker wuerde einen fertigen
 * Zeitraum stattdessen verlaengern.
 */
import { useState, type ReactNode } from 'react';
import { DayPicker } from 'react-day-picker';
import { de } from 'date-fns/locale/de';
import { Button } from '@/shared/components';
import { DayPickerChevron, dayPickerRangeClassNames } from '@/shared/components/dayPickerTheme';
import { useMediaQuery } from '@/shared/hooks/useMediaQuery';
import { normalizeRange } from './rangeSelectionModel';
import { nightsLabel, rangeChipLabel } from './calendarFormat';
import {
  isSelectableRange,
  lastSelectableDay,
  nightsInRange,
  toIsoDay,
  toLocalDate,
  type IsoRange,
} from './rangePickerModel';

interface RangeCalendarProps {
  today: string;
  initial?: IsoRange;
  applyLabel: string;
  header?: ReactNode;
  canApply?: boolean;
  /** Feste Monatszahl, wo die Breite nicht dem Fenster folgt — etwa in einer Seitenleiste (§9). */
  months?: 1 | 2;
  onApply: (range: IsoRange) => void;
  onCancel: () => void;
}

export function RangeCalendar({
  today,
  initial,
  applyLabel,
  header,
  canApply = true,
  months,
  onApply,
  onCancel,
}: RangeCalendarProps) {
  const mobile = useMediaQuery('(max-width: 767px)');
  const [range, setRange] = useState<IsoRange | null>(initial ?? null);
  const [anchor, setAnchor] = useState<string | null>(null);
  const complete = range !== null && anchor === null && isSelectableRange(range, today);

  function pick(day: string) {
    if (anchor === null) {
      setAnchor(day);
      setRange({ from: day, to: day });
      return;
    }
    setRange(normalizeRange(anchor, day));
    setAnchor(null);
  }

  return (
    <div className="space-y-4">
      {header}
      <DayPicker
        mode="range"
        locale={de}
        weekStartsOn={1}
        numberOfMonths={months ?? (mobile ? 1 : 2)}
        defaultMonth={toLocalDate(initial?.from ?? today)}
        startMonth={toLocalDate(today)}
        endMonth={toLocalDate(lastSelectableDay(today))}
        selected={range ? { from: toLocalDate(range.from), to: toLocalDate(range.to) } : undefined}
        onSelect={(_, triggerDate) => pick(toIsoDay(triggerDate))}
        disabled={[
          { before: toLocalDate(today) },
          { after: toLocalDate(lastSelectableDay(today)) },
        ]}
        classNames={dayPickerRangeClassNames}
        components={{ Chevron: DayPickerChevron }}
      />
      <div className="border-line/50 flex flex-wrap items-center justify-between gap-3 border-t pt-3">
        <p className="text-ink text-[13px]" aria-live="polite">
          {anchor !== null ? (
            <span className="text-ink-soft">Ende wählen</span>
          ) : range ? (
            <>
              <span className="font-semibold">{rangeChipLabel(range.from, range.to)}</span>
              <span className="text-ink-soft"> · {nightsLabel(nightsInRange(range))}</span>
            </>
          ) : (
            <span className="text-ink-soft">Beginn wählen</span>
          )}
        </p>
        <div className="flex gap-2">
          {/* type="button": der Kalender sitzt auch in Formularen und darf keines absenden. */}
          <Button
            type="button"
            variant="ghost"
            className="border-line bg-card border"
            onClick={onCancel}
          >
            Abbrechen
          </Button>
          <Button
            type="button"
            variant="primary"
            disabled={!complete || !canApply}
            onClick={() => complete && onApply(range)}
          >
            {applyLabel}
          </Button>
        </div>
      </div>
    </div>
  );
}
